import { useMemo } from "react";
import { useAppSelector } from "../../../app/hooks";
import { useGetUserInfoQuery } from "../../../app/rootReducer";
import { useGetProductsQuery } from "../../../common/api/product";
import { ProductIndexItemInterface } from "../../../common/interfaces/apiV2/shared/ProductIndexItemInterface";
import { buildProductRangeOptionsQuery } from "../../../common/utils/productRange/buildProductRangeOptionsQuery";

interface UseCategoryProductsParams {
    categoryId: number;
}

interface UseCategoryProductsResult {
    products: ProductIndexItemInterface[];
    isLoading: boolean;
}

export const useCategoryProducts = ({
    categoryId,
}: UseCategoryProductsParams): UseCategoryProductsResult => {
    const { openedCategoryIds, selectedFilters } = useAppSelector(
        (state) => state.productRange,
    );
    const optionsQuery = buildProductRangeOptionsQuery(selectedFilters);
    const { data: userData } = useGetUserInfoQuery();
    const countryId = userData?.activeCountry?.id;
    const isRequested = openedCategoryIds.includes(categoryId);

    const {
        data: productsResponse,
        isFetching,
        isLoading,
    } = useGetProductsQuery(
        {
            categoryIds: [categoryId],
            options: optionsQuery,
            countryId: countryId ?? 0,
        },
        { skip: !isRequested || !countryId },
    );

    const products = useMemo((): ProductIndexItemInterface[] => {
        const group = productsResponse?.data.find(
            (entry) => entry.category_id === categoryId,
        );

        return group?.products ?? [];
    }, [categoryId, productsResponse?.data]);

    const isCategoryLoading =
        isRequested && (isLoading || isFetching) && products.length === 0;

    return {
        products,
        isLoading: isCategoryLoading,
    };
};
